import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { QuestionRatio, DigitalSubmissionRatio, QuestionTypeV2 } from '../../../redux/types';
import useDebounce from '../../../hooks/useDebounce';
import useQuestionSaveStatus, { QuestionSaveOp } from '../../../hooks/survey/useQuestionSaveStatus';

export type Props ={
  enrollmentId?: number;
  sectionId?: number;
  caseId?: number;
  question: QuestionRatio;
  disabled?: boolean;
  defaultSubmission?: DigitalSubmissionRatio;
  required?: boolean;
  onSave?: (val: DigitalSubmissionRatio|Omit<DigitalSubmissionRatio, 'id'>|undefined) => Promise<void>;
  getSaveStatus?: (op: QuestionSaveOp, qId: number, caseId?: number) => void;
  manualSave?: boolean;
  saveOnBlur?: boolean;
}

const numberRegex = /(^\d+):(\d+)$/;

const RatioQuestionInput: React.FC<Props> = ({
  enrollmentId,
  sectionId,
  caseId,
  question,
  disabled,
  defaultSubmission,
  required,
  onSave,
  getSaveStatus,
  manualSave,
  saveOnBlur,
}: Props) => {
  const [saveStatus, setSaveStatus] = useQuestionSaveStatus(question.id, caseId);
  const [answer, setAnswer] = useState<string>(defaultSubmission?.answer.join(':') || '');
  const [error, setError] = useState<React.ReactElement | string>();

  useEffect(() => {
    setAnswer(defaultSubmission?.answer.join(':') || '');
  }, [defaultSubmission, caseId]);

  useEffect(() => {
    setSaveStatus('idle');
    setError(undefined);
  }, [caseId, sectionId, enrollmentId, setSaveStatus]);

  useEffect(() => {
    if (getSaveStatus) {
      getSaveStatus(saveStatus.op, saveStatus.questionId, saveStatus.caseId);
    }
  }, [getSaveStatus, saveStatus]);

  const getAnswerObj = (newAns: string): DigitalSubmissionRatio|Omit<DigitalSubmissionRatio, 'id'>|undefined => {
    if (!enrollmentId || !sectionId) {
      return undefined;
    }
    let ans: [number, number] | ['RTU' | '-'] = ['-'];
    if (newAns === 'RTU' || newAns === '-') {
      ans = [newAns];
    } else {
      ans = [Number(newAns.split(':')[0]), Number(newAns.split(':')[1])];
    }
    if (defaultSubmission) {
      return {
        ...defaultSubmission,
        answer: ans,
      } as DigitalSubmissionRatio;
    }
    const newAnswer: Omit<DigitalSubmissionRatio, 'id'> = {
      answer: ans,
      digitalQuestionId: question.id,
      digitalSectionId: sectionId,
      enrollmentId,
      type: QuestionTypeV2.Ratio,
      digitalCaseId: caseId,
    };
    return newAnswer;
  };

  const validateAndSave = (value: string) => {
    if (!onSave) {
      return;
    }
    if (numberRegex.test(value) || value === 'RTU' || value === '-') {
      const answerObj = getAnswerObj(value);
      setSaveStatus('saving');
      onSave(answerObj)
        .then(() => {
          setSaveStatus('saved');
          setError(undefined);
        })
        .catch((e) => {
          setError((e as Error).message);
          setSaveStatus('idle');
        });
    } else {
      setError(<FormattedMessage id="ratioQuestionInput.invalidNumber" />);
    }
  };

  const debounceSaveHandler = (debouncedAnswer: string) => {
    if (saveStatus.op === 'saving') {
      return;
    }
    validateAndSave(debouncedAnswer);
  };

  const saveOnClickHandler = () => {
    if (!manualSave) {
      return;
    }
    validateAndSave(answer);
  };

  const saveOnBlurHandler = () => {
    if (!saveOnBlur) {
      return;
    }
    validateAndSave(answer);
  };

  const saveDebounce = useDebounce<string>(debounceSaveHandler, 1000);

  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (saveStatus.op === 'saving') {
      return;
    }
    const { value } = e.target;
    setAnswer(value);
    setError(undefined);
    if (!manualSave && !saveOnBlur) {
      setSaveStatus('debounceStarting');
      saveDebounce(value);
    }
  };

  return (
    <Box>
      <Typography fontWeight={500} gutterBottom>
        {question.text}
      </Typography>
      <Box>
        <TextField
          size="small"
          id="number-question-input"
          placeholder={question.placeholder || ''}
          value={answer}
          onChange={onTextChange}
          fullWidth
          disabled={disabled}
          required={required}
          error={!!error}
          autoComplete="off"
          onBlur={saveOnBlurHandler}
          onFocus={() => setSaveStatus('focused')}
        />
      </Box>
      {
        saveStatus.op === 'saved' && saveStatus.caseId === caseId && (
          <Box display="flex">
            <Typography color="primary.main" justifyContent="flex-end" variant="caption">
              <FormattedMessage id="submission.saved" />
            </Typography>
          </Box>
        )
      }
      {
        saveStatus.op === 'saving' && saveStatus.caseId === caseId && (
          <Box display="flex">
            <Typography justifyContent="flex-end" variant="caption">
              <FormattedMessage id="submission.saving" />
            </Typography>
          </Box>
        )
      }
      {
        error && (
          <Typography color="secondary" variant="caption">{error}</Typography>
        )
      }
      {
        manualSave && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={saveStatus.op === 'saving'}
              onClick={saveOnClickHandler}
              startIcon={saveStatus.op === 'saving' && (
                <CircularProgress sx={{ color: 'primary.dark' }} size={18} />
              )}
            >
              <FormattedMessage id="save" />
            </Button>
          </Box>
        )
      }
    </Box>
  );
};

export default RatioQuestionInput;
